import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tooltip,
  TextField,
  Collapse,
  MenuItem,
  Select,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingDown as TrendingDownIcon,
  InfoOutlined as InfoIcon,
  WarningAmber as WarningIcon,
  EditOutlined as EditIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MORTGAGE_RATES } from '../../data/mortgageRates';
import { calculateMortgageScore } from '../../utils/mortgageScore';

/* ── Logo map for lenders ──
   Prefer icon-optimized versions (square/compact) over full wordmarks
   so logos remain recognizable at 24-32px.                            */
const LENDER_LOGOS = {
  'BMO': '/logos/bmo.svg',
  'CIBC': '/logos/cibc-icon.webp',
  'RBC': '/logos/rbc-icon.webp',
  'TD': '/logos/td.svg',
  'Scotiabank': '/logos/scotiabank-icon.webp',
  'Desjardins': '/logos/desjardins.svg',
  'National Bank': '/logos/national-bank.svg',
  'Tangerine': '/logos/tangerine-icon.webp',
  'Simplii Financial': '/logos/simplii-icon.webp',
  'Canwise (Ratehub)': '/logos/canwise.png',
  'MCAP': '/logos/mcap.svg',
  'First National': '/logos/first-national.svg',
  'Nesto': '/logos/nesto.svg',
  'True North': '/logos/true-north.svg',
};

/* ── Tooltip key mapping ── */
const TOOLTIP_KEYS = {
  tasaFija: 'mortgageRates.tooltips.fixedRate',
  tasaVariable: 'mortgageRates.tooltips.variableRate',
  primeRate: 'mortgageRates.tooltips.primeRate',
  bocRate: 'mortgageRates.tooltips.bocRate',
  postedRate: 'mortgageRates.tooltips.postedRate',
  specialRate: 'mortgageRates.tooltips.specialRate',
  asegurada: 'mortgageRates.tooltips.insured',
  ltv: 'mortgageRates.tooltips.ltv',
  cerrada: 'mortgageRates.tooltips.closed',
  abierta: 'mortgageRates.tooltips.open',
  pagoMensual: 'mortgageRates.tooltips.monthlyPayment',
  finmapleScore: 'mortgageRates.tooltips.finmapleScore',
};

/* ── Shared table styles ── */
const tableContainerSx = {
  border: '1px solid #E2E6EB',
  borderRadius: '14px',
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(30,58,95,0.2) transparent',
  '&::-webkit-scrollbar': { height: 4 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(30,58,95,0.2)', borderRadius: 4 },
  '& .MuiTable-root': { minWidth: 500 },
};

const headCellSx = {
  bgcolor: '#F0F2F5',
  fontWeight: 700,
  fontSize: '13px',
  color: '#1E3A5F',
  py: 1.75,
  px: 2.5,
  whiteSpace: 'nowrap',
  borderBottom: '2px solid #D9DEE4',
  letterSpacing: '0.01em',
  textTransform: 'none',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  boxShadow: '0 1px 0 #E5E7EB',
};

const cellSx = {
  fontSize: '14px',
  color: '#1A1A1A',
  py: 2,
  px: 2.5,
  borderBottom: '1px solid #EEF0F3',
  lineHeight: 1.5,
  fontVariantNumeric: 'tabular-nums',
};

/* ── Alternating row stripe ── */
const zebraRow = (i) => ({
  '&:last-child td': { borderBottom: 0 },
  ...(i % 2 === 1 && { bgcolor: '#FAFBFC' }),
  transition: 'background-color 100ms ease',
  '&:hover': { bgcolor: '#F0F4F8' },
});

const rateFields = ['variable5', 'fixed1', 'fixed2', 'fixed3', 'fixed4', 'fixed5', 'fixed7', 'fixed10'];

const DATE_LOCALES = { es: 'es-CA', en: 'en-CA', fr: 'fr-CA' };
const formatDate = (dateStr, lang = 'es') => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(DATE_LOCALES[lang] || 'es-CA', { day: 'numeric', month: 'long', year: 'numeric' });
};

const getColumnMin = (banks, field) => {
  const vals = banks.map((b) => b[field]).filter((v) => v != null);
  return vals.length ? Math.min(...vals) : null;
};

/* ── Info tooltip icon ── */
const InfoTip = ({ text, tKey, t: tFn }) => {
  const title = tKey && tFn ? tFn(tKey) : text;
  return (
    <Tooltip title={title} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={3000}>
      <InfoIcon sx={{ fontSize: 14, color: '#9CA3AF', ml: 0.5, cursor: 'help', verticalAlign: 'text-bottom' }} />
    </Tooltip>
  );
};

/* ── Lender logo + name ── */
const LenderName = ({ name, showWarning, t: tFn }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    {LENDER_LOGOS[name] ? (
      <Box
        component="img"
        src={LENDER_LOGOS[name]}
        alt={name}
        loading="lazy"
        sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: '6px', flexShrink: 0 }}
      />
    ) : (
      <Box
        sx={{
          width: 32, height: 32, borderRadius: '6px', bgcolor: '#EEF0F3',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '13px', fontWeight: 700, color: '#5F6B7A', flexShrink: 0,
        }}
      >
        {name.charAt(0)}
      </Box>
    )}
    <Box component="span" sx={{ fontWeight: 600, fontSize: '14px', lineHeight: 1.2 }}>{name}</Box>
    {showWarning && tFn && (
      <Tooltip title={tFn('mortgageRates.notAvailableQC')} arrow>
        <WarningIcon sx={{ fontSize: 14, color: '#E65100' }} />
      </Tooltip>
    )}
  </Box>
);

/* ── Score badge (0-100 scale) ── */
const ScoreBadge = ({ score }) => {
  let bgcolor, color, borderColor;
  if (score >= 90) { bgcolor = '#E8F5E9'; color = '#2E7D32'; borderColor = '#C8E6C9'; }
  else if (score >= 80) { bgcolor = '#F1F8E9'; color = '#558B2F'; borderColor = '#DCEDC8'; }
  else if (score >= 70) { bgcolor = '#FFF8E1'; color = '#E6A100'; borderColor = '#FFECB3'; }
  else if (score >= 60) { bgcolor = '#FFF3E0'; color = '#E65100'; borderColor = '#FFE0B2'; }
  else { bgcolor = '#F7F9FC'; color = '#5F6B7A'; borderColor = '#EEF0F3'; }

  return (
    <Chip
      label={score}
      size="small"
      sx={{
        bgcolor, color, fontWeight: 700, fontSize: '13px',
        height: 28, minWidth: 44,
        border: `1px solid ${borderColor}`,
      }}
    />
  );
};

/* ── Section heading ── */
const SectionHeading = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ scrollMarginTop: '80px' }}>
    <Typography
      variant="h4"
      component="h2"
      sx={{ fontWeight: 700, color: '#1A1A1A', mb: 0.5, fontSize: { xs: '20px', md: '24px' } }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

/* ── Collapsible accordion section ── */
const AccordionSection = ({ title, trackingId, children }) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev && window.plausible) {
        window.plausible('Mortgage Accordion Open', { props: { section: trackingId } });
      }
      return !prev;
    });
  }, [trackingId]);

  return (
    <Box sx={{ borderTop: '1px solid #EEF0F3' }}>
      <Button
        onClick={toggle}
        aria-expanded={open}
        fullWidth
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        }
        sx={{
          textTransform: 'none', color: '#1E3A5F', fontWeight: 600, fontSize: '14px',
          justifyContent: 'space-between', py: 2, px: 0,
          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
        }}
      >
        {title}
      </Button>
      <Collapse in={open} timeout={300}>
        <Box sx={{ pb: 3 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ── Header key mapping for rate matrix ── */
const HEADER_KEY_MAP = {
  'Banco': 'bank', 'Variable 5yr': 'variable5yr',
  'Fijo 1yr': 'fixed1yr', 'Fijo 2yr': 'fixed2yr', 'Fijo 3yr': 'fixed3yr',
  'Fijo 4yr': 'fixed4yr', 'Fijo 5yr': 'fixed5yr', 'Fijo 7yr': 'fixed7yr', 'Fijo 10yr': 'fixed10yr',
};

/* ── Rate matrix table (posted/special) ── */
const RateMatrix = ({ data, highlightLowest, t: tFn }) => {
  const mins = {};
  if (highlightLowest) {
    rateFields.forEach((f) => { mins[f] = getColumnMin(data.banks, f); });
  }

  const localizedHeaders = data.headers.map((h) => {
    const key = HEADER_KEY_MAP[h];
    return key ? tFn(`mortgageRates.rateMatrixHeaders.${key}`) : h;
  });

  return (
    <TableContainer sx={tableContainerSx}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {localizedHeaders.map((h) => (
              <TableCell key={h} sx={headCellSx}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.banks.map((bank, i) => (
            <TableRow key={bank.name} sx={zebraRow(i)}>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                <LenderName name={bank.name} showWarning={bank.name === 'Simplii Financial'} />
              </TableCell>
              {rateFields.map((f) => {
                const val = bank[f];
                const isMin = highlightLowest && val != null && val === mins[f];
                return (
                  <TableCell
                    key={f}
                    sx={{
                      ...cellSx,
                      fontWeight: isMin ? 700 : 400,
                      color: isMin ? '#2E7D32' : val == null ? '#9CA3AF' : '#1A1A1A',
                    }}
                  >
                    {val != null ? `${val.toFixed(2)}%` : '\u2014'}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/* ══════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════ */
/* ── Payment calculator helper ── */
const calcMonthlyPayment = (principal, annualRate, amortYears) => {
  const r = annualRate / 100 / 12;
  const n = amortYears * 12;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
};

const PROVINCES = ['Quebec', 'Ontario', 'BC', 'Alberta', 'Manitoba', 'Saskatchewan', 'Nova Scotia', 'New Brunswick', 'PEI', 'Newfoundland'];

const MortgageRatesSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tTerm = (term) => t(`mortgageRates.termLabels.${term}`, term);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [amount, setAmount] = useState(400000);
  const [downPercent, setDownPercent] = useState(20);
  const [amort, setAmort] = useState(25);
  const [province, setProvince] = useState('Quebec');
  const isInsured = downPercent < 20;
  const rates = MORTGAGE_RATES;

  const quebecSorted = [...rates.quebec.bestRates].sort((a, b) => a.rate - b.rate);

  /* Filter out generic "Big 6 Bank" and "Trust Company", sort by rate */
  const canadaSorted = [...rates.canada.fiveYearFixed]
    .filter((r) => r.lender !== 'Big 6 Bank' && r.lender !== 'Trust Company')
    .sort((a, b) => a.rate - b.rate);

  const bestCanadaRate = canadaSorted.length ? canadaSorted[0].rate : 3.69;

  const desjClosed = [...rates.desjardins.closed].sort((a, b) => {
    const aSpec = a.term.includes('especial') ? 0 : 1;
    const bSpec = b.term.includes('especial') ? 0 : 1;
    return aSpec - bSpec || a.rate - b.rate;
  });

  return (
    <>
      {/* ════════════════════════════════════════════
          Mejores tasas disponibles en Québec
          ════════════════════════════════════════════ */}
      <Box id="tasas-quebec" component="section" sx={{ bgcolor: '#FFFFFF', py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            title={t('mortgageRates.sections.quebecTitle')}
            subtitle={t('mortgageRates.sections.quebecSubtitle')}
          />

          <TableContainer sx={tableContainerSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>{t('mortgageRates.table.term')}</TableCell>
                  <TableCell sx={headCellSx}>{t('mortgageRates.table.bestRate')}</TableCell>
                  <TableCell sx={headCellSx}>{t('mortgageRates.table.source')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quebecSorted.map((r, i) => (
                  <TableRow key={r.term} sx={zebraRow(i)}>
                    <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                      {tTerm(r.term)}
                      {r.term.includes('variable') && <InfoTip tKey={TOOLTIP_KEYS.tasaVariable} t={t} />}
                      {r.term.includes('fijo') && !r.term.includes('variable') && <InfoTip tKey={TOOLTIP_KEYS.tasaFija} t={t} />}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: '#2E7D32', fontWeight: 700, fontSize: '15px' }}>
                      {r.rate.toFixed(2)}%
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box
                        sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 1,
                          bgcolor: '#F7F9FC', border: '1px solid #EEF0F3',
                          borderRadius: '8px', px: 1.5, py: 0.5, height: 34,
                        }}
                      >
                        {LENDER_LOGOS[r.source] ? (
                          <Box
                            component="img"
                            src={LENDER_LOGOS[r.source]}
                            alt={r.source}
                            loading="lazy"
                            sx={{ width: 24, height: 24, objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 24, height: 24, borderRadius: '4px', bgcolor: '#EEF0F3',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '11px', fontWeight: 700, color: '#5F6B7A', flexShrink: 0,
                            }}
                          >
                            {r.source.charAt(0)}
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1 }}>
                          {r.source}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════
          Comparar por banco: 5 años fijo
          ════════════════════════════════════════════ */}
      <Box id="tasas-canada" component="section" sx={{ bgcolor: '#F7F9FC', py: { xs: 4, md: 5 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            title={t('mortgageRates.sections.canadaTitle')}
            subtitle={t('mortgageRates.sections.canadaSubtitle')}
          />

          {/* Scenario editor card */}
          <Box
            sx={{
              bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px',
              px: { xs: 2, md: 2.5 }, py: { xs: 1.5, md: 2 }, mb: 3,
            }}
          >
            {/* Summary row + edit button */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '13px', color: '#5F6B7A', fontWeight: 500 }}>
                {t('mortgageRates.payment.calculatedWith')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                <Chip
                  label={`$${amount.toLocaleString('en-CA')}`}
                  size="small"
                  sx={{ bgcolor: '#F0F2F5', color: '#1E3A5F', fontWeight: 700, fontSize: '13px', height: 28 }}
                />
                <Chip
                  label={`${downPercent}% enganche`}
                  size="small"
                  sx={{ bgcolor: '#F0F2F5', color: '#1E3A5F', fontWeight: 600, fontSize: '12px', height: 28 }}
                />
                <Chip
                  label={`${amort} ${t('mortgageRates.payment.years')}`}
                  size="small"
                  sx={{ bgcolor: '#F0F2F5', color: '#1E3A5F', fontWeight: 600, fontSize: '12px', height: 28 }}
                />
                <Chip
                  label={isInsured ? t('mortgageRates.payment.insured') : t('mortgageRates.payment.uninsured')}
                  size="small"
                  sx={{
                    bgcolor: isInsured ? '#FFF3E0' : '#E8F5E9',
                    color: isInsured ? '#E65100' : '#2E7D32',
                    fontWeight: 600, fontSize: '11px', height: 24,
                  }}
                />
                <Chip
                  label={province}
                  size="small"
                  sx={{ bgcolor: '#F0F2F5', color: '#5F6B7A', fontWeight: 500, fontSize: '12px', height: 28 }}
                />
              </Box>
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setScenarioOpen((prev) => !prev);
                  if (window.plausible) window.plausible('Scenario Editor Toggle', { props: { page: 'hipotecas' } });
                }}
                sx={{
                  textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#1E3A5F',
                  borderRadius: '8px', border: '1px solid #EEF0F3', px: 1.5, ml: 'auto',
                  '&:hover': { bgcolor: '#F7F9FC', borderColor: '#1E3A5F' },
                }}
              >
                {t('mortgageRates.payment.editScenario')}
              </Button>
            </Box>

            {/* Expandable editor */}
            <Collapse in={scenarioOpen} timeout={250}>
              <Box
                sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, pt: 2,
                  borderTop: '1px solid #EEF0F3', alignItems: 'flex-end',
                }}
              >
                <Box sx={{ minWidth: 140 }}>
                  <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mb: 0.5, fontWeight: 500 }}>
                    {t('mortgageRates.payment.mortgageAmount')}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value) || 0)}
                    InputProps={{ sx: { fontSize: '13px', height: 36 } }}
                    sx={{ width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mb: 0.5, fontWeight: 500 }}>
                    {t('mortgageRates.payment.downPayment')}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={downPercent}
                    onChange={(e) => setDownPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    InputProps={{ sx: { fontSize: '13px', height: 36 } }}
                    sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  />
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mb: 0.5, fontWeight: 500 }}>
                    {t('mortgageRates.payment.amortization')}
                  </Typography>
                  <Select
                    size="small"
                    value={amort}
                    onChange={(e) => setAmort(Number(e.target.value))}
                    sx={{ fontSize: '13px', height: 36, borderRadius: '8px', minWidth: 80 }}
                  >
                    {[15, 20, 25, 30].map((y) => (
                      <MenuItem key={y} value={y}>{y} {t('mortgageRates.payment.years')}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mb: 0.5, fontWeight: 500 }}>
                    {t('mortgageRates.payment.province')}
                  </Typography>
                  <Select
                    size="small"
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                    sx={{ fontSize: '13px', height: 36, borderRadius: '8px', minWidth: 130 }}
                  >
                    {PROVINCES.map((p) => (
                      <MenuItem key={p} value={p}>{p}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setScenarioOpen(false)}
                  sx={{
                    height: 36, px: 2.5, fontSize: '13px', fontWeight: 700,
                    borderRadius: '8px', bgcolor: '#1E3A5F', color: '#fff',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1A3152' },
                  }}
                >
                  {t('mortgageRates.payment.applyScenario')}
                </Button>
              </Box>
            </Collapse>
          </Box>

          <TableContainer sx={tableContainerSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>{t('mortgageRates.table.bank')}</TableCell>
                  <TableCell sx={headCellSx}>{t('mortgageRates.table.rate5yrFixed')}</TableCell>
                  <TableCell sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>
                    {t('mortgageRates.table.monthlyPayment')}<InfoTip tKey={TOOLTIP_KEYS.pagoMensual} t={t} />
                  </TableCell>
                  <TableCell sx={headCellSx}>
                    <Box>
                      {t('mortgageRates.table.finmapleScore')}<InfoTip tKey="mortgagePage.score.tooltip" t={t} />
                      <Typography component="span" sx={{ display: 'block', fontSize: '10px', fontWeight: 400, color: '#9CA3AF', mt: 0.25 }}>
                        {t('mortgagePage.score.helper')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {canadaSorted.map((r, i) => {
                  const score = calculateMortgageScore(r.lender, r.rate, bestCanadaRate);
                  const mortgageAmount = amount * (1 - downPercent / 100);
                  const displayPayment = Math.round(calcMonthlyPayment(mortgageAmount, r.rate, amort));
                  return (
                    <TableRow
                      key={`${r.lender}-${i}`}
                      sx={{
                        ...(i === 0
                          ? {
                              bgcolor: '#EDF7EE',
                              borderLeft: '3px solid #2E7D32',
                              '&:hover': { bgcolor: '#E4F5E6' },
                              '&:last-child td': { borderBottom: 0 },
                            }
                          : zebraRow(i)),
                      }}
                    >
                      <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                        <LenderName name={r.lender} showWarning={r.lender === 'Simplii Financial'} t={t} />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: i === 0 ? '#2E7D32' : '#1A1A1A', fontWeight: 700, fontSize: '15px' }}>
                        {r.rate.toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ ...cellSx, display: { xs: 'none', sm: 'table-cell' }, color: '#5F6B7A' }}>
                        ${displayPayment.toLocaleString()}{t('mortgageRates.payment.perMonth')}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <ScoreBadge score={score.total} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, gap: 1 }}>
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
              {t('mortgageRates.payment.footnoteCustom', { amount: amount.toLocaleString('en-CA'), dp: downPercent, years: amort })}
            </Typography>
            <Typography
              component="button"
              onClick={() => {
                if (window.plausible) window.plausible('Score Methodology Click', { props: { page: 'hipotecas' } });
              }}
              sx={{
                fontSize: '12px', color: '#1E3A5F', fontWeight: 600,
                background: 'none', border: 'none', cursor: 'pointer', p: 0,
                textDecoration: 'underline', textDecorationColor: 'rgba(30,58,95,0.3)',
                '&:hover': { textDecorationColor: '#1E3A5F' },
              }}
            >
              {t('mortgagePage.score.methodology')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ════════════════════════════════════════════
          Collapsed accordions: Posted, Desjardins, Nesto
          ════════════════════════════════════════════ */}
      <Box component="section" sx={{ bgcolor: '#FFFFFF', py: { xs: 2, md: 3 } }}>
        <Container maxWidth="lg">
          {/* Posted vs Special rates */}
          <AccordionSection
            title={t('mortgagePage.accordion.postedRates')}
            trackingId="posted"
          >
            <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3 }}>
              {t('mortgageRates.sections.postedSubtitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: '16px', mb: 2 }}>
                  {t('mortgageRates.table.postedRates')}<InfoTip tKey={TOOLTIP_KEYS.postedRate} t={t} />
                </Typography>
                <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <RateMatrix data={rates.postedRates} highlightLowest t={t} />
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: '16px', mb: 2 }}>
                  <TrendingDownIcon sx={{ fontSize: 18, color: '#2E7D32', mr: 0.5, verticalAlign: 'text-bottom' }} />
                  {t('mortgageRates.table.specialRates')}<InfoTip tKey={TOOLTIP_KEYS.specialRate} t={t} />
                </Typography>
                <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <RateMatrix data={rates.specialRates} highlightLowest t={t} />
                </Box>
              </Box>
            </Box>
          </AccordionSection>

          {/* Desjardins detail */}
          <AccordionSection
            title={t('mortgagePage.accordion.desjardins')}
            trackingId="desjardins"
          >
            <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3 }}>
              {t('mortgageRates.sections.desjardinsSubtitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1A1A1A', mb: 1.5 }}>
                  {t('mortgageRates.table.closedMortgage')}<InfoTip tKey={TOOLTIP_KEYS.cerrada} t={t} />
                </Typography>
                <TableContainer sx={tableContainerSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>{t('mortgageRates.desjardinsHeaders.term')}</TableCell>
                        <TableCell sx={headCellSx}>{t('mortgageRates.desjardinsHeaders.rate')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {desjClosed.map((r, i) => (
                        <TableRow key={r.term} sx={zebraRow(i)}>
                          <TableCell sx={{ ...cellSx, fontWeight: r.term.includes('especial') ? 700 : 500 }}>
                            {tTerm(r.term)}
                            {r.term.includes('especial') && (
                              <Chip
                                label={t('mortgageRates.special')}
                                size="small"
                                sx={{ ml: 1, bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '12px', height: 20 }}
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              ...cellSx, fontWeight: 700, fontSize: '15px',
                              color: r.term.includes('especial') ? '#2E7D32' : '#1A1A1A',
                            }}
                          >
                            {r.rate.toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1A1A1A', mb: 1.5 }}>
                  {t('mortgageRates.table.openMortgage')}<InfoTip tKey={TOOLTIP_KEYS.abierta} t={t} />
                </Typography>
                <TableContainer sx={tableContainerSx}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>{t('mortgageRates.desjardinsHeaders.term')}</TableCell>
                        <TableCell sx={headCellSx}>{t('mortgageRates.desjardinsHeaders.rate')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rates.desjardins.open.map((r, i) => (
                        <TableRow key={r.term} sx={zebraRow(i)}>
                          <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{tTerm(r.term)}</TableCell>
                          <TableCell sx={{ ...cellSx, fontWeight: 700, fontSize: '15px' }}>{r.rate.toFixed(2)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          </AccordionSection>

          {/* Nesto LTV advanced */}
          <AccordionSection
            title={t('mortgagePage.accordion.nestoLTV')}
            trackingId="nesto_ltv"
          >
            <Typography sx={{ color: '#5F6B7A', fontSize: '14px', lineHeight: 1.6, mb: 3 }}>
              {t('mortgageRates.sections.nestoSubtitle')}
              <InfoTip tKey={TOOLTIP_KEYS.ltv} t={t} />
            </Typography>
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <TableContainer sx={tableContainerSx}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>{t('mortgageRates.nestoHeaders.term')}</TableCell>
                      <TableCell sx={headCellSx}>{t('mortgageRates.nestoHeaders.ltv65')}</TableCell>
                      <TableCell sx={headCellSx}>{t('mortgageRates.nestoHeaders.ltv70')}</TableCell>
                      <TableCell sx={headCellSx}>{t('mortgageRates.nestoHeaders.ltv75')}</TableCell>
                      <TableCell sx={headCellSx}>{t('mortgageRates.nestoHeaders.ltv80')}</TableCell>
                      <TableCell sx={headCellSx}>
                        {t('mortgageRates.nestoHeaders.insured')}<InfoTip tKey={TOOLTIP_KEYS.asegurada} t={t} />
                      </TableCell>
                      <TableCell sx={headCellSx}>{t('mortgageRates.nestoHeaders.uninsured')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rates.nesto.rates.map((r, i) => (
                      <TableRow key={r.term} sx={zebraRow(i)}>
                        <TableCell sx={{ ...cellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>{tTerm(r.term)}</TableCell>
                        {['ltv65', 'ltv70', 'ltv75', 'ltv80', 'insured', 'uninsured'].map((f) => (
                          <TableCell
                            key={f}
                            sx={{
                              ...cellSx,
                              color: r[f] == null ? '#9CA3AF' : '#1A1A1A',
                              fontWeight: f === 'insured' ? 700 : 400,
                            }}
                          >
                            {r[f] != null ? `${r[f].toFixed(2)}%` : '\u2014'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </AccordionSection>
        </Container>
      </Box>
    </>
  );
};

export default MortgageRatesSection;
